
import { Component, Prop, Vue } from 'vue-property-decorator';
import { ValidationProvider } from 'vee-validate';
import ToggleSwitch from '@/components/shared/ToggleSwitch.vue';
import InfoBanner from '@/components/shared/suites/InfoBanner.vue';
import Tabs from '@/components/shared/Tabs.vue';
import TextField from '@/components/shared/TextField.vue';

type ValidationMode = 'builder' | 'regex';

@Component<PaymentMethodValidationForm>({
	components: {
		ToggleSwitch,
		InfoBanner,
		Tabs,
		TextField,
		ValidationProvider
	}
})
export default class PaymentMethodValidationForm extends Vue {
	@Prop({ type: Object, required: true }) private paymentMethod!: RestaurantExtraPaymentMethod;
	@Prop({ type: Boolean, required: false, default: false }) private disabled!: boolean;

	private enabled: boolean = !!this.paymentMethod.validation;
	private validatePrefix: boolean = this.paymentMethod.validation?.prefix !== undefined;
	private validateCharacterLimit: boolean = this.paymentMethod.validation?.characterLength !== undefined;

	private get validation(): ExtraPaymentMethodValidation | undefined {
		return this.paymentMethod.validation;
	}

	private get selectedMode(): ValidationMode {
		return this.validation?.mode ?? 'builder';
	}

	private get modeTabs(): Tab[] {
		return [
			{ key: 'builder', label: this.$t('settings.payment_validation_rules.builder'), disabled: this.disabled },
			{ key: 'regex', label: this.$t('settings.payment_validation_rules.raw_regex'), disabled: this.disabled }
		];
	}

	private created(): void {
		if (this.validation) {
			this.validation.mode === 'regex' ? this.applyRegexMode() : this.applyBuilderMode();
		}
	}

	/**
	 * Enable/Disable validation: seed the default (builder) config or drop it entirely.
	 *
	 * @param {boolean} isToggled
	 * @return {void}
	 */
	private onEnableToggle(isToggled: boolean): void {
		if (isToggled) {
			Vue.set(this.paymentMethod, 'validation', { mode: 'builder' });
			this.applyBuilderMode(true);
		}
		else {
			Vue.delete(this.paymentMethod, 'validation');
		}
	}

	/**
	 * Reset the target mode's properties to their initial values.
	 *
	 * @param {Tab} tab
	 * @return {void}
	 */
	private onModeChange(tab: Tab): void {
		const mode: ValidationMode = tab.key === 'regex' ? 'regex' : 'builder';
		if (!this.validation || this.selectedMode === mode) {
			return;
		}
		this.validation.mode = mode;
		mode === 'regex' ? this.applyRegexMode(true) : this.applyBuilderMode(true);
	}

	/**
	 * Add or remove the prefix property from the validation object
	 *
	 * @param {boolean} isToggled
	 * @return {void}
	 */
	private onValidatePrefixToggle(isToggled: boolean): void {
		if (!this.validation) {
			return;
		}
		isToggled ? Vue.set(this.validation, 'prefix', this.validation.prefix ?? '') : Vue.delete(this.validation, 'prefix');
	}

	/**
	 * Add or remove the characterLength property from the validation object
	 *
	 * @param {boolean} isToggled
	 * @return {void}
	 */
	private onValidateCharacterLimitToggle(isToggled: boolean): void {
		if (!this.validation) {
			return;
		}
		isToggled ? Vue.set(this.validation, 'characterLength', this.validation.characterLength ?? null) : Vue.delete(this.validation, 'characterLength');
	}

	/**
	 * Initiate the builder properties and remove the regex-mode ones while also setting the toggles' state.
	 *
	 * @param {boolean} resetFields
	 * @return {void}
	 */
	private applyBuilderMode(resetFields: boolean = false): void {
		if (!this.validation) {
			return;
		}
		if (resetFields) {
			this.validatePrefix = false;
			this.validateCharacterLimit = false;
		}
		Vue.delete(this.validation, 'regex');
		Vue.delete(this.validation, 'flags');
		Vue.set(this.validation, 'caseSensitive', this.validation.caseSensitive ?? false);
		this.onValidatePrefixToggle(this.validatePrefix);
		this.onValidateCharacterLimitToggle(this.validateCharacterLimit);
	}

	/**
	 * Initiate the regex property and remove the builder-mode ones
	 *
	 * @param {boolean} resetFields
	 * @return {void}
	 */
	private applyRegexMode(resetFields: boolean = false): void {
		if (!this.validation) {
			return;
		}
		['prefix', 'characterLength', 'caseSensitive', 'flags'].forEach(key => Vue.delete(this.validation!, key));
		Vue.set(this.validation, 'regex', resetFields ? '' : (this.validation.regex ?? ''));
	}
}
